"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NumberTicker } from "@/components/ui/number-ticker";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/lib/supabase";
import { RefreshCcwIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

type SystemPanelProps = {
  onBack?: () => void;
};

type MetricKey =
  | "users"
  | "teams"
  | "agents"
  | "prompts"
  | "chats"
  | "messages"
  | "modelsTotal"
  | "modelsActive";

type SystemMetrics = Record<MetricKey, number>;

const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? "0.1.0";
const APP_ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT ?? "unknown";
const USER_FILE_SIZE_LIMIT_RAW =
  process.env.NEXT_PUBLIC_USER_FILE_SIZE_LIMIT ?? "0";

const formatBytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return "Not set";
  }

  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(value) / Math.log(1024)),
    units.length - 1
  );
  const scaled = value / 1024 ** exponent;

  return `${scaled.toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
};

const EMPTY_METRICS: SystemMetrics = {
  users: 0,
  teams: 0,
  agents: 0,
  prompts: 0,
  chats: 0,
  messages: 0,
  modelsTotal: 0,
  modelsActive: 0,
};

const metricItems: Array<{ key: MetricKey; label: string }> = [
  { key: "users", label: "Users" },
  { key: "teams", label: "Teams" },
  { key: "agents", label: "Agents" },
  { key: "prompts", label: "Prompts" },
  { key: "chats", label: "Chats" },
  { key: "messages", label: "Messages" },
  { key: "modelsTotal", label: "Models" },
  { key: "modelsActive", label: "Active models" },
];

type SystemHealthResponse = {
  metrics: SystemMetrics;
  generatedAt: string;
};

export function SystemPanel({ onBack }: SystemPanelProps) {
  const [metrics, setMetrics] = useState<SystemMetrics>(EMPTY_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } =
        await supabase.functions.invoke<SystemHealthResponse>(
          "get-system-health"
        );

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      if (!data?.metrics) {
        throw new Error("Invalid response from get-system-health.");
      }

      setMetrics(data.metrics);
      setLastUpdatedAt(data.generatedAt ?? new Date().toISOString());
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? `Unable to load secure system metrics: ${loadError.message}`
          : "Unable to load system health metrics."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  const formattedUpdatedAt = useMemo(() => {
    if (!lastUpdatedAt) {
      return "Never";
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(lastUpdatedAt));
  }, [lastUpdatedAt]);

  const statusLabel = error ? "Degraded" : "Healthy";
  const userFileLimitBytes = Number.parseInt(USER_FILE_SIZE_LIMIT_RAW, 10);
  const userFileLimitLabel = formatBytes(userFileLimitBytes);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="System"
        description="System details and health metrics for users, models, and team activity."
        onBack={onBack}
        actions={
          <div className="flex w-full items-center justify-end">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => void loadMetrics()}
              disabled={loading}
            >
              <RefreshCcwIcon className="size-4" />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Application</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Runtime information and overall system status.
                </p>
              </div>
              <Badge
                variant={error ? "destructive" : "secondary"}
                className={!error ? "bg-activated text-activated-foreground" : undefined}
              >
                {statusLabel}
              </Badge>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Version</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">v{APP_VERSION}</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{statusLabel}</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Last updated
                </p>
                <p className="mt-2 text-sm font-medium text-foreground">{formattedUpdatedAt}</p>
              </div>
            </CardContent>
          </Card>

          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Unable to load all metrics</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Health metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-2/3">Metric</TableHead>
                    <TableHead className="w-1/3 text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metricItems.map((metric) => (
                    <TableRow key={metric.key}>
                      <TableCell className="font-medium">{metric.label}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {loading ? (
                          <Skeleton className="ml-auto h-5 w-12 rounded" />
                        ) : (
                          <NumberTicker
                            value={metrics[metric.key]}
                            className="text-foreground tracking-normal"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-2/3">Variable</TableHead>
                    <TableHead className="w-1/3 text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">NEXT_PUBLIC_ENVIRONMENT</TableCell>
                    <TableCell className="text-right">{APP_ENVIRONMENT}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      NEXT_PUBLIC_USER_FILE_SIZE_LIMIT
                    </TableCell>
                    <TableCell className="text-right">
                      {Number.isFinite(userFileLimitBytes) && userFileLimitBytes > 0
                        ? `${userFileLimitLabel} (${userFileLimitBytes.toLocaleString()} bytes)`
                        : "Set NEXT_PUBLIC_USER_FILE_SIZE_LIMIT"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
